/*************************
 * @license
 * Copyright 2024 Myenergi Ltd. All rights reserved.
 * No part of this work may be reproduced, stored in a retrieval system of any nature, or transmitted, in any form or by any means without the prior written permission of Myenergi Ltd., the copyright owner.
 * If any unauthorised acts are carried out in relation to this copyright work, a civil claim for damages may be made and/or a criminal prosecution may result.
 *************************/
import { action, makeObservable, observable } from "mobx";
import { persist } from "mobx-persist";
import { GetUsersAndRolesQuery as GetUsersAndRoles } from "models/graphql";

type User = GetUsersAndRoles["users"][number];

const keysToFilter = ["email", "name"];

const usersFiltered = (filter: string, users: User[] = []) =>
  users.filter((user) =>
    keysToFilter.some((key) =>
      user[key as keyof User]
        ?.toString()
        .toLowerCase()
        .includes(filter.toLowerCase()),
    ),
  );

export class UsersStore {
  public constructor() {
    makeObservable(this);

    this.usersSearchTerm = "";
    this.usersAndRolesFiltered = {
      getUserRoles: [],
      users: [],
    };
  }

  @observable @persist("object") public usersAndRolesFiltered: GetUsersAndRoles;
  @observable @persist("object") public usersSearchTerm: string;
  @observable @persist("object") public usersAndRoles:
    | GetUsersAndRoles
    | undefined;
  @observable @persist public isLoading = false;

  @action.bound
  public setIsLoading(isLoading: boolean): void {
    if (isLoading) {
      this.usersAndRoles = undefined;
    }

    this.isLoading = isLoading;
  }

  @action.bound
  public setUsersAndRoles(data: GetUsersAndRoles): void {
    this.usersAndRoles = data;
    this.setUsersFiltered();
  }

  @action.bound
  public setUsersFiltered(): void {
    const users = usersFiltered(
      this.usersSearchTerm,
      this.usersAndRoles?.users,
    );

    this.usersAndRolesFiltered = {
      getUserRoles: this.usersAndRoles?.getUserRoles || [],
      users,
    };
  }

  @action.bound
  public setUsersSearchTerm(searchTerm: string): void {
    this.usersSearchTerm = searchTerm;
    this.setUsersFiltered();
  }
}
